import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  createHttpLink,
} from "@apollo/client";
import type { NormalizedCacheObject } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { AppContextProvider } from "./context/AppContext";
import { BrowserRouter } from "react-router-dom";
import { auth } from "./firebase";
import { onAuthStateChanged, type User } from "firebase/auth";
import { ThemeContextProvider } from "./context/ThemeContext";
import { BlogProvider } from "./context/BlogsContext";
import { initializeGA } from "./utility/analytics.utils";
import { HelmetProvider } from "react-helmet-async";

const AppWithApollo: React.FC = () => {
  const [client, setClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);

  // Initialize Google Analytics (only in production)
  useEffect(() => {
    initializeGA();
    
    // Set up global error tracking (only in production)
    if (process.env.NODE_ENV === "production") {
      const handleError = (event: ErrorEvent) => {
        if (window.gtag) {
          const { trackError } = require("./utility/analytics.utils");
          trackError(new Error(event.message), {
            error_file: event.filename,
            error_line: event.lineno,
            error_column: event.colno,
          });
        }
      };
      
      const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
        if (window.gtag) {
          const { trackError } = require("./utility/analytics.utils");
          const error = event.reason instanceof Error 
            ? event.reason 
            : new Error(String(event.reason));
          trackError(error, {
            error_type: "unhandled_promise_rejection",
          });
        }
      };
      
      window.addEventListener("error", handleError);
      window.addEventListener("unhandledrejection", handleUnhandledRejection);
      
      return () => {
        window.removeEventListener("error", handleError);
        window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      };
    }
  }, []);

  useEffect(() => {
    // Use environment variable for GraphQL URI (dev/prod configured in .env files)
    const getGraphQLUri = () => {
      if (process.env.REACT_APP_GRAPHQL_URI) {
        return process.env.REACT_APP_GRAPHQL_URI;
      }
      // Fallback: use relative path for local development
      return "/api/graphql";
    };

    const httpLink = createHttpLink({
      uri: getGraphQLUri(),
    });

    const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
      // Create authLink that fetches fresh token for each request
      const authLink = setContext(async (_, { headers }) => {
        // Get fresh token for each request to avoid expired tokens
        const currentUser = auth.currentUser;
        const token = currentUser ? await currentUser.getIdToken(true) : null;
        
        return {
        headers: {
          ...headers,
          Authorization: token ? `Bearer ${token}` : "",
        },
        };
      });

      const apolloClient = new ApolloClient({
        link: authLink.concat(httpLink),
        cache: new InMemoryCache(),
      });

      setClient(apolloClient);
    });

    return () => unsubscribe();
  }, []);

  if (!client) {
    return null;
  }

  return (
        <HelmetProvider>
          <ApolloProvider client={client}>
            <BrowserRouter>
              <ThemeContextProvider>
                <AppContextProvider>
                  <BlogProvider>
                    <App />
                  </BlogProvider>
                </AppContextProvider>
              </ThemeContextProvider>
            </BrowserRouter>
          </ApolloProvider>
        </HelmetProvider>
  );
};

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Failed to find the root element");
}

const root = ReactDOM.createRoot(rootElement);
root.render(<AppWithApollo />);
